import { rtkApi } from '@/shared/api/rtkApi/rtkApi';
import { GET_DASHBOARD, GET_ADMIN } from '@/shared/api/rtkApi/tags';
import { ApiResponse } from '@/shared/types/common';
import { createFormData } from '@/shared/lib/utils/createFormData';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import { adminActions } from '../model/slice/adminSlice';
import {
  IAdminPost,
  IncomeParams,
  IDashboardSummaryResponse,
  IDashboardIncomeResponse,
  AdminProfileResponse,
  UpdateAdminProfileRequest,
  UpdateAdminProfileResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from './types';

const ENTITY_URL = `${import.meta.env.VITE_APP_API_URL}/admin`;

export const adminApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    storeAdmin: build.mutation<ApiResponse, IAdminPost>({
      query: (data) => ({
        url: ENTITY_URL,
        method: 'POST',
        body: createFormData(data),
      }),
      // invalidatesTags: [GET_ADMINS],
    }),
    getAdminSession: build.query<{ isAuth: boolean }, void>({
      query: () => ({
        url: `${import.meta.env.VITE_APP_API_URL_V1_ADMIN}/authentication/session`,
      }),
      transformResponse: (response: { validity: boolean }) => ({ isAuth: response.validity }),
    }),
    getDashboard: build.query<IDashboardSummaryResponse, void>({
      query: () => ({
        url: '/admin/dashboard',
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getDashboardIncome: build.query<IDashboardIncomeResponse, IncomeParams>({
      query: (params) => ({
        url: '/admin/dashboard/income',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getMyProfile: build.query<AdminProfileResponse, void>({
      query: () => ({
        url: '/my-profile',
      }),
      providesTags: [GET_ADMIN],
    }),
    updateAdminProfile: build.mutation<UpdateAdminProfileResponse, UpdateAdminProfileRequest>({
      queryFn: async (body) => {
        const formData = new FormData();

        if (body.firstName) formData.append('firstName', body.firstName);
        if (body.lastName) formData.append('lastName', body.lastName);
        if (body.phone) formData.append('phone', body.phone);
        if (body.agreedToMarketing !== undefined) {
          formData.append('agreedToMarketing', String(body.agreedToMarketing));
        }
        if (body.department) formData.append('department', body.department);
        if (body.avatar) formData.append('avatar', body.avatar);

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/my-profile`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_ADMIN],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(adminActions.setProfile(data.data.user));
        } catch {
          // Error handling is done by RTK Query
        }
      },
    }),
    changePassword: build.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (body) => ({
        url: '/my-profile/change-password',
        method: 'PUT',
        body: {
          currentPassword: body.currentPassword,
          newPassword: body.newPassword,
        },
      }),
    }),
  }),
});

export const {
  useStoreAdminMutation,
  useGetDashboardQuery,
  useGetDashboardIncomeQuery,
  useGetMyProfileQuery,
  useUpdateAdminProfileMutation,
  useChangePasswordMutation,
  endpoints: { getAdminSession, getMyProfile },
} = adminApi;
